import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";

// Images (your paths)
import healthTech from "../images/health-tech.png";
import edTech from "../images/edtech.webp";
import finTech from "../images/fintech.webp";
import greenTech from "../images/green-tech.png";
import retail from "../images/retail.png";
import eCommerce from "../images/ecommerce-technicmentors.avif";

export default function Industries() {
  const [activeTab, setActiveTab] = useState("health-tech");

  const primary = "#684df4";

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-out",
    });
  }, []);

  const industries = [
    {
      id: "health-tech",
      title: "HealthTech",
      image: healthTech,
      text: (
        <>
          <p>
            We develop cutting-edge HealthTech solutions that transform patient care and clinical workflows. Our platforms integrate AI-driven diagnostics, real-time monitoring, and secure data exchange to help healthcare providers deliver faster, more accurate, and personalized treatment.
          </p>
          <p>
            From telemedicine systems with HD video and secure chat to full-featured Electronic Health Records (EHR/EMR), we build tools that reduce administrative burden and let doctors focus on what matters most — patient outcomes.
          </p>
          <p>
            Additional capabilities include remote patient monitoring (RPM), wearable integration, chronic disease management dashboards, appointment scheduling with smart reminders, and HIPAA/GDPR-compliant infrastructure.
          </p>
          <p>
            Our mission is to make high-quality healthcare more accessible, efficient, and human-centered — no matter where the patient or provider is located.
          </p>
        </>
      ),
    },
    {
      id: "ed-tech",
      title: "EdTech",
      image: edTech,
      text: (
        <>
          <p>
            We create modern EdTech ecosystems that make learning engaging, personalized, and accessible at scale. Our solutions combine interactive content, adaptive learning paths powered by AI, and seamless collaboration tools for students, teachers, and institutions.
          </p>
          <p>
            Full-featured Learning Management Systems (LMS), virtual classrooms with live polling & breakout rooms, gamified quizzes, automated grading, and progress analytics help educators save time while improving student engagement and results.
          </p>
          <p>
            We also build mobile-first educational apps, AR/VR learning modules, content authoring tools, and parent portals — supporting K-12, higher education, corporate training, and lifelong learning environments.
          </p>
          <p>
            By bridging traditional teaching with digital innovation, we help institutions prepare students for the future while keeping education inclusive and enjoyable.
          </p>
        </>
      ),
    },
    {
      id: "fin-tech",
      title: "FinTech",
      image: finTech,
      text: (
        <>
          <p>
            We engineer next-generation FinTech platforms that deliver secure, fast, and delightful financial experiences. From mobile-first digital banking and neobank apps to embedded finance and open-banking APIs, we cover the full spectrum of modern finance.
          </p>
          <p>
            Our solutions include digital wallets, instant payment gateways (UPI, SEPA, ACH), peer-to-peer transfers, investment & robo-advisory tools, BNPL (Buy Now Pay Later) engines, and cryptocurrency on-ramps — all built with bank-grade security.
          </p>
          <p>
            Advanced fraud detection (ML-based), KYC/AML compliance modules, real-time transaction monitoring, and rich financial analytics dashboards help institutions stay ahead of risk while delighting users.
          </p>
          <p>
            Secure. Compliant. Scalable. User-first — ready for the future of finance.
          </p>
        </>
      ),
    },
    {
      id: "green-tech",
      title: "GreenTech",
      image: greenTech,
      text: (
        <>
          <p>
            We build software that powers the green transition — helping businesses measure, reduce, and report their environmental impact with transparency and precision.
          </p>
          <p>
            Carbon footprint calculators, energy monitoring & optimization dashboards, smart building/IoT integration, supply-chain sustainability tracking, and ESG reporting tools are at the core of our GreenTech portfolio.
          </p>
          <p>
            We also develop solutions for renewable energy asset management (solar, wind, battery storage), waste reduction analytics, water usage optimization, and circular economy platforms — turning sustainability goals into measurable business value.
          </p>
          <p>
            Technology that protects the planet while driving profitability and regulatory compliance.
          </p>
        </>
      ),
    },
    {
      id: "retail",
      title: "Retail",
      image: retail,
      text: (
        <>
          <p>
            We deliver intelligent retail technology that creates exceptional shopping experiences — both in-store and online. Our omnichannel solutions connect physical and digital touchpoints for seamless customer journeys.
          </p>
          <p>
            Next-gen POS systems, real-time inventory & supply-chain visibility, smart shelf & RFID integration, personalized promotions via AI, loyalty programs, and customer behavior analytics help retailers sell more and waste less.
          </p>
          <p>
            We also provide headless commerce backends, in-store digital signage, queue management, staff task management apps, and predictive demand forecasting — empowering modern retail operations at any scale.
          </p>
          <p>
            From small boutiques to large chains — we help you delight customers and stay competitive in a fast-changing market.
          </p>
        </>
      ),
    },
    {
      id: "e-commerce",
      title: "E-Commerce",
      image: eCommerce,
      text: (
        <>
          <p>
            We craft high-performance e-commerce platforms that convert visitors into loyal customers. Our solutions combine beautiful design, lightning-fast performance, and powerful backend logic to scale with your business.
          </p>
          <p>
            Full-stack development (headless or traditional), multi-vendor marketplaces, subscription models, B2B portals, international selling (multi-currency, multi-language), and advanced search & merchandising tools are part of our expertise.
          </p>
          <p>
            Seamless payment gateway integrations, shipping & logistics APIs, abandoned cart recovery, personalized recommendations, dynamic pricing, and rich analytics help maximize revenue.
          </p>
          <p>
            Mobile-first, SEO-optimized, conversion-focused — built to grow with you in the competitive digital marketplace.
          </p>
        </>
      ),
    },
  ];

  return (
    <div>
      <Helmet>
        <title>Industries We Empower | Technic Mentors</title>
        <link rel="canonical" href="https://technicmentors.com/industries/" />
      </Helmet>

      {/* Hero */}
      <div className="allHeaderBg">
        <div className="allHeader-overlay d-flex align-items-center justify-content-center">
          <div className="text-center">
            <h1 className="head text-white animate__animated animate__zoomIn allHeaderP">
              Industries We Empower
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-5 py-lg-5" style={{ background: "#f9f9ff" }}>
        <div className="container">
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="fw-bold mb-2" style={{ color: primary, fontSize: "2.6rem" }}>
              Our Industry Expertise
            </h2>
            <p className="text-muted fw-normal mb-4">
              Building powerful, future-ready digital products for the world's most dynamic sectors
            </p>
          </div>

          {/* Compact Tabs */}
          <div className="d-flex justify-content-center mb-5">
            <div className="d-flex flex-wrap gap-2 gap-md-3 justify-content-center" style={{ maxWidth: "1100px" }}>
              {industries.map((item) => (
                <button
                  key={item.id}
                  className="btn rounded-pill fw-medium text-nowrap"
                  onClick={() => setActiveTab(item.id)}
                  style={{
                    padding: "6px 18px",                    // ← much shorter vertically
                    fontSize: "0.92rem",                    // smaller text
                    lineHeight: "1.4",
                    background: activeTab === item.id ? primary : "white",
                    color: activeTab === item.id ? "white" : primary,
                    border: `2px solid ${primary}`,
                    minWidth: "110px",                      // compact width
                    transform: activeTab === item.id ? "translateY(-4px) scale(1.02)" : "translateY(0)",
                    boxShadow:
                      activeTab === item.id
                        ? "0 10px 25px rgba(104,77,244,0.22)"
                        : "0 3px 10px rgba(0,0,0,0.06)",
                    transition: "all 0.32s ease",
                  }}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {industries.map((item) =>
            activeTab === item.id ? (
              <div
                key={item.id}
                className="row justify-content-center align-items-center g-5"
                data-aos="fade-up"
                data-aos-duration="900"
              >
                <div className="col-lg-6 order-lg-2 text-center" data-aos="fade-right" data-aos-delay="200">
                  <div className="shadow-xl rounded-4 overflow-hidden border-0">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-100"
                      style={{
                        objectFit: "cover",
                        aspectRatio: "4/3",
                        borderRadius: "20px",
                      }}
                    />
                  </div>
                </div>

                <div className="col-lg-6 order-lg-1" data-aos="fade-left" data-aos-delay="100">
                  <div className="pe-lg-4">
                    <h4 className="fw-bold mb-4" style={{ color: primary, fontSize: "1.9rem" }}>
                      {item.title} Solutions
                    </h4>
                    {item.text}
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}